import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import Masonry from "react-masonry-css";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/ProjectPage.scss";

interface ProjectImage {
  url: string;
  event: string;
  category: string;
  eventId: string;
}

const ProjectPage: React.FC = () => {
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [filter, setFilter] = useState<string>("All");
  const [visibleCount, setVisibleCount] = useState(8);
  const [selectedImage, setSelectedImage] = useState<ProjectImage | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchImages = async () => {
      const q = query(collection(db, "events"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const allImages: ProjectImage[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.images && Array.isArray(data.images)) {
          data.images.forEach((img: any) => {
            allImages.push({
              url: img.url || img,
              event: data.event,
              category: data.category || "Uncategorized",
              eventId: doc.id,
            });
          });
        }
      });
      setImages(allImages);
    };
    fetchImages();
  }, []);

  const filteredImages = images.filter((img) =>
    filter === "All" ? true : img.category === filter
  );

  const visibleImages = filteredImages.slice(0, visibleCount);

  const breakpointColumnsObj = {
    default: 3,
    1100: 2,
    700: 1,
  };

  return (
    <div className="projects-page">
      <motion.h1
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        MODEL EVENT PROJECTS
      </motion.h1>

      <motion.div className="filters" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {["All", "Fashion", "Editorial", "Commercial"].map((cat) => (
          <button
            key={cat}
            className={filter === cat ? "active" : ""}
            onClick={() => setFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </motion.div>

      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="masonry-grid"
        columnClassName="masonry-grid_column"
      >
        {visibleImages.map((img, index) => (
          <motion.div
            key={index}
            className="image-card"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            onClick={() => setSelectedImage(img)}
          >
            <img src={img.url} alt={`Event: ${img.event}`} />
            <span>{img.event}</span>
          </motion.div>
        ))}
      </Masonry>

      {visibleCount < filteredImages.length && (
        <div className="load-more">
          <button onClick={() => setVisibleCount((prev) => prev + 6)}>View More</button>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selectedImage.url} alt="Large preview" />
              <h3>{selectedImage.event}</h3>
              <p>Category: {selectedImage.category}</p>
              <button onClick={() => navigate(`/projects/${selectedImage.eventId}`)}>
                View Project
              </button>
              <span className="close-btn" onClick={() => setSelectedImage(null)}>×</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectPage;
